import { useCallback, useMemo, useState, useRef } from 'react';
import { useDebounce } from 'hooks/useDebounce';
import { hasValue } from 'swipeclock-frontend';
import { useFreeTrialContext } from 'providers/FreeTrialProvider';

export const useFreeTrialValidateOnStart = ({
  ids,
  validationCallback,
  page,
  values,
}) => {
  const { state, actions } = useFreeTrialContext();
  const bag = values || state.formBag[page];

  return useMemo(() => {
    let valid = true;
    const errors = [];

    ids.forEach((id) => {
      const value = bag?.[id];
      const validationError = validationCallback(id, value);
      // Push errors to an array to be set later
      errors.push({ id, validationError, page });
      if (validationError) {
        valid = false;
      }
    });

    if (hasValue(errors)) {
      // Cannot reset from provider and set errors in the same render cycle
      // So we use a timeout to set the errors after the render cycle
      setTimeout(() => {
        errors.forEach((error) => {
          actions.setError(error.id, error.validationError, error.page);
        });
      }, 0);
    }

    return { valid, errors };
  }, []);
};

export const useFreeTrialInputHandler = ({ ids, validationCallback, page }) => {
  // Integrate LoginProvider to access global state and actions.
  const { state, actions } = useFreeTrialContext();
  const validationRef = useRef({});

  // Initialize errors from global state.
  const errors = state.errors[page] || {};

  // Convert error messages into booleans for each input to track their validity.
  // We use this to determine if the input should be dirty or not.
  const errorBooleans = Object.keys(errors).reduce((acc, key) => {
    acc[key] = !!errors[key];
    return acc;
  }, {});

  // Initialize 'dirty' state for each input based on their existing errors.
  // dirty looks like this: { id: true }
  const [dirty, setDirty] = useState(errorBooleans);

  // Helper method to get the error for a given id
  // It takes an id and returns a string used as a error message
  const getError = (id) => {
    return errors?.[id] || null;
  };

  // Helper method to check if the form is valid
  // It only checks if valid or not and does not set any state
  const isValid = (options) => {
    const { dirtied = false } = options || {};

    let filteredIds = ids;

    // If dirtied is true, then we only want to validate inputs that have been dirtied.
    // This prevents checking on every input change.
    // Why is this important? Because say we want to validate something that is required
    // but the value is currenlty an empty string. We don't want to show the error message
    // sometimes like if the user navigates back to page 1 and the input is empty.
    if (dirtied) {
      filteredIds = ids.filter((id) => dirty[id]);
    }

    // Finds the first input that has an error from the validationRef object of inputs
    // ------------
    // Check if any input has an error message.
    // The loop will break if an error is found.
    // The variable 'hasValidationError' will be set to true if any input has an error.
    // We return the opposite of 'hasValidationError' to indicate that the form is valid if no errors are found.
    const hasValidationError = filteredIds?.some((id) => {
      const value = validationRef.current[id]?.value;
      const validationError = validationCallback(id, value, validationRef);
      const hasError = hasValue(validationError);
      return hasError;
    });

    // Return opposite of hasValidationError for isValid
    return !hasValidationError;
  };

  // The 'disabled' state controls the 'Submit' button's availability based on form validity.
  const [disabled, setDisabled] = useState(true);

  // Update the 'disabled' state every time the form undergoes changes.
  const checkFormValidity = (event) => {
    if (!event?.target) return;
    event.preventDefault();
    setDisabled(!isValid());
  };

  // This is used under the hood of the handleBlur and handleChange methods.
  const handleInput = (event) => {
    if (!event?.target) return;
    event.preventDefault();
    const { id, value } = event.target;
    const validationError = validationCallback(id, value, validationRef);
    actions.setError(id, validationError, page);
  };

  const handleBlur = useCallback(
    (id) => (event) => {
      handleInput(event);
      // Let's check if we should dirty the input
      // We only want to dirty if the input on blur is valid
      // Guard: first check if dirty, if dirty we bail.
      if (dirty[id]) return;
      // If not dirty, set dirty to true
      setDirty((prevDirty) => ({
        ...prevDirty,
        [id]: true,
      }));
      // In handleInput, we cover valiation for the current input, but
      // we also need to check if the whole form is valid.
      checkFormValidity(event);
    },
    []
  );

  // Debouncing is applied to improve performance and limit unnecessary validations.
  const handleDebouncedValidation = useDebounce(checkFormValidity);

  // Combines debounced and immediate validation based on the 'dirty' state.
  const handleChange = (id) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useCallback(
      (event) => {
        // Only run the debounced input handler if the input is dirty.
        if (dirty[event.target?.id]) {
          // Not debouncing due to issues with auto-fill scenarios
          // where the error doesn't clear right away.
          // Validation is still on debounce which will help performance.
          // In testing, this seems to work fine for Free Trial, but may need to be revisited
          // for other forms.
          handleInput(event);
        }
        handleDebouncedValidation(event);
      },
      [dirty[id]]
    );

  const handleSelect = useCallback(
    (id) => (ref) => {
      const validationError = validationCallback(id, ref?.value, validationRef);

      actions.setError(id, validationError, page);
      // If required can update any other state/context here too.
      setDisabled(!isValid());
    },
    []
  );

  return {
    disabled,
    getError,
    handleBlur,
    handleChange,
    handleSelect,
    isValid, // should be used to confirm if the whole form is valid
    validationRef,
    setDirty,
  };
};
